import { ActionIcon, Avatar, Badge, Group, Loader, Menu, Stack, Text, Tooltip } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Icon } from "@mdi/react";
import { decorativeIconProps, SEMANTIC_ICONS } from "@/components/icons";
import type { CurrentPlan } from "@/features/subscriptions/queries";

interface HeaderProfileProps {
  name: string;
  plan?: CurrentPlan;
  email: string;
  imageUrl: string | null;
  active: boolean;
  settingsActive: boolean;
  mobileAppsActive: boolean;
  mcpActive?: boolean;
  signOutPending: boolean;
  signOutError: string | null;
  onSignOut: () => void;
}

export function HeaderProfile({ name, plan, email, imageUrl, active, settingsActive, mobileAppsActive, mcpActive, signOutPending, signOutError, onSignOut }: HeaderProfileProps) {
  const [opened, setOpened] = useState(false);

  return (
    <Group gap="xs" align="center" wrap="nowrap" miw={0} maw="100%" style={{ flex: "1 1 auto" }}>
      <Stack gap={0} miw={0} maw={{ base: 220, sm: 280 }} ta="right" style={{ flex: "1 1 auto", overflow: "hidden" }}>
        <Group gap={6} justify="flex-end" wrap="nowrap" miw={0}>
          {plan ? <Badge size="xs" variant="light" color={plan.id === "free" ? "gray" : "secondary"} tt="none" aria-label={`${plan.name} account`} title={`${plan.name} account`} style={{ flexShrink: 0, maxWidth: "45%" }}>{plan.name}</Badge> : null}
          <Text size="sm" fw={500} lh={1.3} truncate title={name} miw={0} style={{ flex: "1 1 auto" }}>{name}</Text>
        </Group>
        <Text size="xs" c="dimmed" lh={1.3} truncate title={email}>{email}</Text>
      </Stack>
      <Menu opened={opened} onChange={setOpened} position="bottom-end" width={220} shadow="md" loop={false} withinPortal={false} trapFocus={false} menuItemTabIndex={0}>
        <Tooltip label="Account menu" disabled={opened} events={{ hover: true, focus: true, touch: false }} interactive>
          <Menu.Target>
            <ActionIcon variant="subtle" size={44} radius="xl" aria-label={`Account menu for ${name}`} style={{ flexShrink: 0 }}>
              <Avatar
                size={40}
                name={name}
                src={imageUrl}
                alt=""
                aria-hidden="true"
                color="initials"
                imageProps={{ referrerPolicy: "no-referrer" }}
              />
            </ActionIcon>
          </Menu.Target>
        </Tooltip>
        <Menu.Dropdown>
          <Menu.Item
            renderRoot={(props) => <Link {...props} to="/profile" />}
            aria-current={active ? "page" : undefined}
            mih={44}
            leftSection={<Icon path={SEMANTIC_ICONS.profile} size="20px" {...decorativeIconProps} />}
          >Profile</Menu.Item>
          <Menu.Item
            renderRoot={(props) => <Link {...props} to="/settings" />}
            aria-current={settingsActive ? "page" : undefined}
            mih={44}
            leftSection={<Icon path={SEMANTIC_ICONS.settings} size="20px" {...decorativeIconProps} />}
          >Settings</Menu.Item>
          <Menu.Item
            renderRoot={(props) => <Link {...props} to="/mobile-apps" />}
            aria-current={mobileAppsActive ? "page" : undefined}
            mih={44}
            leftSection={<Icon path={SEMANTIC_ICONS.mobileApps} size="20px" {...decorativeIconProps} />}
          >Apps and Integrations</Menu.Item>
          <Menu.Item
            renderRoot={(props) => <Link {...props} to="/mcp" />}
            aria-current={mcpActive ? "page" : undefined}
            mih={44}
            leftSection={<Icon path={SEMANTIC_ICONS.connection} size="20px" {...decorativeIconProps} />}
          >Connect AI assistant</Menu.Item>
          <Menu.Divider />
          <Menu.Item
            onClick={onSignOut}
            closeMenuOnClick={false}
            disabled={signOutPending}
            aria-busy={signOutPending}
            leftSection={signOutPending ? <Loader size="xs" aria-label="Signing out" /> : <Icon path={SEMANTIC_ICONS.signOut} size="20px" {...decorativeIconProps} />}
            mih={44}
          >Sign out</Menu.Item>
          {signOutError ? <Menu.Label><Text role="alert" c="red" size="sm">{signOutError}</Text></Menu.Label> : null}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
